import { Check } from "lucide-react";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { Eyebrow, SectionTitle } from "../components/Eyebrow";
import { FinalCta } from "../components/FinalCta";
import { CaseStudiesCarousel } from "../components/tarifs/CaseStudiesCarousel";

const PF_URL = "https://www.parlonsfinance.fr/articles/optimiser-ses-frais-bancaires-le-gisement-de-tresorerie-oublie-des-pme-et-eti";

const TarifsHero = () => (
    <section className="bg-navy text-cream">
        <div className="mx-auto max-w-[1040px] px-4 md:px-8 lg:px-12 py-12 md:py-[72px] lg:py-24 flex flex-col gap-[22px] animate-fade-up">
            <div className="text-[13px] font-semibold uppercase tracking-[0.16em] text-gold">Tarifs</div>
            <h1 className="m-0 font-bold text-[40px] md:text-[60px] lg:text-[76px] leading-[1.08] tracking-[-0.03em] text-cream">
                50 % : <span className="text-gold">c'est tout.</span>
            </h1>
            <div className="w-40 h-1.5 bg-gold" />
            <p className="m-0 text-[17px] md:text-xl leading-relaxed text-cream max-w-[760px]">
                Nous ne nous rémunérons qu'au succès de la mission sur 50 % des économies générées par l'optimisation de vos frais bancaires.
            </p>
            <p className="m-0 text-[15px] leading-relaxed text-on-navy-soft max-w-[760px]">
                N'allez pas plus loin sur cette page, il n'y a pas d'astérisque ni de conditions cachées. (Vous pouvez quand même aller plus loin pour voir des cas concrets réalisés par FOD.)
            </p>
            <ul className="list-none mt-2 p-0 flex flex-wrap gap-x-7 gap-y-3 text-base font-medium" data-testid="tarifs-guarantees">
                {["En une seule fois", "Pas d'abonnement", "Pas d'engagement de durée"].map((t) => (
                    <li key={t} className="flex items-center gap-2.5">
                        <Check size={18} strokeWidth={2.5} className="text-gold" aria-hidden="true" />
                        {t}
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

const ParlonsFinance = () => (
    <section data-testid="parlons-finance-section" className="bg-cream border-y border-border-default">
        <div className="mx-auto max-w-[1180px] px-4 md:px-8 lg:px-12 py-12 md:py-16 lg:py-20 flex flex-wrap gap-8 items-center">
            <div className="flex-1 basis-[320px] flex flex-col gap-3">
                <Eyebrow>Presse</Eyebrow>
                <SectionTitle>Validé par Parlons Finance.</SectionTitle>
                <a href={PF_URL} target="_blank" rel="noopener noreferrer" aria-label="Article Parlons Finance (nouvel onglet)" className="mt-1.5 inline-flex self-start">
                    <img src="/media/parlons-finance-logo.png" alt="Parlons Finance" width={150} height={48} className="h-12 w-auto" />
                </a>
            </div>
            <article className="flex-[2_1_420px] bg-white border border-border-default rounded-[14px] p-5 md:p-[30px] flex flex-col gap-3 text-navy">
                <span className="text-[13px] md:text-xs font-semibold uppercase tracking-[0.12em] text-gold-text">Article de référence · Parlons Finance</span>
                <h3 className="m-0 text-lg md:text-[22px] font-bold leading-[1.35]">Optimiser ses frais bancaires : le gisement de trésorerie oublié des PME et ETI</h3>
                <span className="text-sm text-grey-text-soft">14 septembre 2026 · par Naqi Hamza</span>
                <a
                    href={PF_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="parlons-finance-link"
                    className="self-start text-[15px] font-semibold underline underline-offset-4 hover:text-gold-text transition-colors inline-flex items-center min-h-[44px]"
                >
                    Lire l'article
                </a>
            </article>
        </div>
    </section>
);

export default function Tarifs() {
    useDocumentMeta({
        title: "Tarifs — Rémunération 100% au succès — FOD by DMB Partners",
        description: "Rémunération 100 % au succès : 50 % des économies bancaires générées sur 1 an. Aucun frais fixe, aucun engagement : vous ne payez que si vous économisez.",
        canonical: "https://www.fodbydmb.com/tarifs",
    });
    return (
        <div data-testid="tarifs-page">
            <TarifsHero />
            <section className="mx-auto max-w-[1180px] px-4 md:px-8 lg:px-12 py-12 md:py-16 lg:py-[88px] flex flex-col gap-7">
                <div className="flex flex-col gap-3">
                    <Eyebrow>Cas clients</Eyebrow>
                    <SectionTitle>Des résultats concrets, mesurés.</SectionTitle>
                </div>
                <CaseStudiesCarousel />
            </section>
            <ParlonsFinance />
            <FinalCta band title="Pourquoi pas vous ?" text="Découvrez en 30 secondes vos potentielles économies." />
        </div>
    );
}
